import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, Copy, HandCoins, HeartHandshake, QrCode, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { Navbar } from "@/components/landing/Hero";

const CATEGORY_DESC = {
  "Persepuluhan": "Memberi 10% dari berkat sebagai wujud syukur dan ketaatan.",
  "Persembahan Syukur": "Ungkapan sukacita atas kebaikan Tuhan dalam hidup Anda.",
  "Misi": "Mendukung pekabaran Injil dan menyelamatkan satu jiwa lagi.",
  "Pembangunan": "Turut membangun rumah Tuhan dan sarana pelayanan.",
  "Diakonia": "Menolong sesama yang membutuhkan melalui pelayanan kasih.",
};

export default function Giving() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/public/giving").then((r) => setData(r.data)).catch(() => setData({ categories: [] }));
    window.scrollTo(0, 0);
  }, []);

  const copy = () => {
    navigator.clipboard?.writeText(data?.bank_account || "");
    toast.success("Nomor rekening disalin");
  };

  const categories = data?.categories || [];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-giving-page">
      <Navbar />

      <section className="relative overflow-hidden bg-slate-900 text-white pt-36 pb-24 px-6">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/80 via-slate-900 to-indigo-900/70" />
        <div className="absolute -top-20 -right-16 w-72 h-72 rounded-full bg-blue-600/20 blur-2xl" />
        <div className="relative max-w-4xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur-md rounded-full px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-amber-300">
            <HandCoins className="w-4 h-4" /> Memberi dengan Sukacita
          </span>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight" data-testid="giving-title">Persembahan</h1>
          <p className="mt-5 max-w-2xl mx-auto text-base md:text-lg text-slate-300 leading-relaxed font-light">
            "Hendaklah masing-masing memberikan menurut kerelaan hatinya, jangan dengan sedih hati atau karena paksaan,
            sebab Allah mengasihi orang yang memberi dengan sukacita." — 2 Korintus 9:7
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 -mt-14 relative z-10 pb-20 space-y-10">
        {/* Categories */}
        <div>
          <h2 className="text-lg font-bold text-slate-900 px-1">Jenis Persembahan</h2>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="giving-categories">
            {categories.map((c) => (
              <div key={c} data-testid={`giving-cat-${c.replace(/\s/g, "-")}`} className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm hover:-translate-y-1 hover:shadow-md transition duration-300">
                <span className="w-11 h-11 rounded-2xl bg-blue-50 text-blue-600 grid place-items-center"><HeartHandshake className="w-5 h-5" /></span>
                <p className="mt-4 font-bold text-slate-900">{c}</p>
                <p className="mt-1 text-sm text-slate-500 leading-relaxed">{CATEGORY_DESC[c] || "Memberi sebagai wujud syukur kepada Tuhan."}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Bank + QRIS */}
        <div className="grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 bg-white rounded-3xl p-7 border border-slate-100 shadow-sm" data-testid="giving-bank-card">
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-2xl bg-slate-900 text-white grid place-items-center"><Building2 className="w-5 h-5" /></span>
              <div><p className="font-bold text-slate-900">Transfer Bank</p><p className="text-sm text-slate-500">Persembahan melalui rekening gereja</p></div>
            </div>
            {data?.bank_account ? (
              <div className="mt-6 rounded-2xl bg-slate-900 text-white p-6">
                <p className="text-[10px] font-bold uppercase tracking-widest text-white/50">Rekening {data.bank_name}</p>
                <div className="mt-2 flex items-center justify-between gap-3">
                  <p className="text-2xl md:text-3xl font-black tabular-nums tracking-wide" data-testid="giving-bank-account">{data.bank_account}</p>
                  <button type="button" onClick={copy} data-testid="giving-copy-account" className="shrink-0 w-11 h-11 rounded-full bg-white/10 grid place-items-center hover:bg-white/20 active:scale-90 transition"><Copy className="w-4 h-4" /></button>
                </div>
                <p className="mt-2 text-sm text-white/60">a.n. {data.bank_holder}</p>
              </div>
            ) : (
              <p className="mt-6 text-sm text-slate-500 bg-slate-50 rounded-2xl p-5" data-testid="giving-bank-empty">Informasi rekening belum tersedia. Silakan hubungi pengurus gereja.</p>
            )}
          </div>

          <div className="lg:col-span-2 bg-white rounded-3xl p-7 border border-slate-100 shadow-sm flex flex-col" data-testid="giving-qris-card">
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-2xl bg-blue-50 text-blue-600 grid place-items-center"><QrCode className="w-5 h-5" /></span>
              <div><p className="font-bold text-slate-900">Scan QRIS</p><p className="text-sm text-slate-500">Semua e-wallet & m-banking</p></div>
            </div>
            <div className="mt-6 flex-1 grid place-items-center">
              {data?.qris_image_url
                ? <img src={fileUrl(data.qris_image_url)} alt="QRIS GIM Church" className="w-full max-w-[16rem] rounded-2xl border border-slate-100" data-testid="giving-qris-image" />
                : <p className="text-sm text-slate-500 text-center" data-testid="giving-qris-empty">QRIS belum tersedia.</p>}
            </div>
          </div>
        </div>

        {/* Online via portal */}
        <div className="rounded-3xl bg-gradient-to-br from-blue-600 to-indigo-700 text-white p-8 shadow-lg" data-testid="giving-portal-cta">
          <div className="flex flex-col md:flex-row md:items-center gap-6 md:justify-between">
            <div className="flex items-start gap-4">
              <span className="w-12 h-12 rounded-2xl bg-white/15 grid place-items-center shrink-0"><ShieldCheck className="w-6 h-6" /></span>
              <div>
                <p className="text-xl font-black">Beri Online & Simpan Riwayat</p>
                <p className="mt-1 text-sm text-blue-100 leading-relaxed max-w-md">Jemaat terdaftar dapat memberi langsung via QRIS/Virtual Account dan melihat riwayat persembahan di Portal Jemaat.</p>
              </div>
            </div>
            <Link to="/portal/persembahan" data-testid="giving-portal-link" className="shrink-0 inline-flex items-center justify-center gap-2 bg-white text-blue-700 font-bold py-3 px-7 rounded-full shadow-md hover:bg-blue-50 active:scale-95 transition">
              Beri via Portal <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        <div className="text-center">
          <Link to="/" data-testid="giving-back-home" className="text-sm font-bold text-slate-500 hover:text-slate-900 transition">← Kembali ke Beranda</Link>
        </div>
      </section>
    </div>
  );
}
